/**
 * UsageWidget - Compact display of tenant usage against plan limits
 */

import { useUsage } from '@/hooks/useUsage';
import { Link } from 'react-router-dom';

interface UsageBarProps {
  label: string;
  used: number;
  max: number;
  percent: number;
  isExceeded: boolean;
}

function UsageBar({ label, used, max, percent, isExceeded }: UsageBarProps) {
  const barColor = isExceeded ? 'bg-red-500' : percent >= 95 ? 'bg-orange-500' : percent >= 80 ? 'bg-yellow-500' : 'bg-blue-500';
  const bgColor = isExceeded ? 'bg-red-100' : 'bg-gray-100';

  return (
    <div className="flex items-center gap-3">
      <span className="text-xs font-medium text-gray-500 w-14">{label}</span>
      <div className={`flex-1 h-1.5 rounded-full ${bgColor} overflow-hidden`}>
        <div
          className={`h-full ${barColor} transition-all duration-300`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
      <span className={`text-xs tabular-nums ${isExceeded ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
        {used.toLocaleString()} / {max.toLocaleString()}
      </span>
    </div>
  );
}

export function UsageWidget() {
  const { data: usage, isLoading, error } = useUsage();

  if (isLoading || error || !usage) {
    return null; // Don't show widget while loading or on error
  }

  const recordsExceeded = usage.records.percent >= 100;
  const sitesExceeded = usage.sites.percent >= 100;
  const showUpgrade = recordsExceeded || sitesExceeded || usage.records.percent >= 80 || usage.sites.percent >= 80;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between gap-6">
        {/* Usage bars */}
        <div className="flex-1 flex items-center gap-6">
          <div className="flex-1 max-w-xs">
            <UsageBar
              label="Records"
              used={usage.records.used}
              max={usage.records.max}
              percent={usage.records.percent}
              isExceeded={recordsExceeded}
            />
          </div>
          <div className="flex-1 max-w-xs">
            <UsageBar
              label="Sites"
              used={usage.sites.used}
              max={usage.sites.max}
              percent={usage.sites.percent}
              isExceeded={sitesExceeded}
            />
          </div>
        </div>

        {/* Status indicator and upgrade */}
        <div className="flex items-center gap-3">
          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded capitalize">
            {usage.plan}
          </span>

          {/* Contextual message */}
          {recordsExceeded && (
            <span className="text-xs text-red-600">Sync paused</span>
          )}
          {!recordsExceeded && sitesExceeded && (
            <span className="text-xs text-orange-600">Site limit reached</span>
          )}

          {showUpgrade && (
            <Link
              to="/settings/billing"
              className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              Upgrade
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default UsageWidget;
